import { Center, Link, Text, VStack } from '@chakra-ui/react';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { useJwt } from 'react-jwt';
import { Link as ReactRouterLink } from 'react-router-dom';
import * as Yup from 'yup';

import { useLEAccordionItemContext } from '../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import LogoutConfirmModal from '../../../components/LogoutConfirmModal';
import Input from '../../../components/formComponents/Input';
import Button from '../../../components/ui/buttons/Button';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import { PHONE_NUMBER_PLACEMENT_VARIATIONS } from '../../../constants/customers';
import { LDFlags } from '../../../constants/flags';
import {
  UNSUPPORTED_LIENHOLDER_SELL_MSG,
  UNSUPPORTED_LIENHOLDER_SELL_SELECT_KEEP_MSG,
} from '../../../constants/tempInfos';
import { ACCOUNT_LOGIN } from '../../../constants/urls';
import {
  useCheckExistingAccountByEmailLazyQuery,
  useTemporaryInfoUpdateMutation,
} from '../../../gql/generated/graphql';
import { useFlag, useStep, useTempInfo } from '../../../hooks';
import { CookieKeys, useCookie } from '../../../hooks/useCookie';
import useIsSellBlockedLienholder from '../../../hooks/useIsSellBlockedLienholder';
import useOptOutLookup from '../../../hooks/useOptOutLookup';
import { usePostLienholderRouting } from '../../../hooks/usePostLienholderRouting';
import { trackLead } from '../../../services/analytics/conversionEvents';
import { RudderEvent, rudderanalytics } from '../../../services/analytics/rudderstack';
import { JWTToken, getEmailFromJWT } from '../../../utils/auth';
import { emailValidationRequired } from '../../../utils/validation/email';
import {
  phoneValidationOptional,
  phoneValidationRequired,
} from '../../../utils/validation/phoneNumber';
import { getInitialWantsToSell } from '../../flowEntryPages/utils';
import KeepMyCarSelector from './KeepMyCarSelector';

type FormValues = {
  email: string;
  phone_number: string;
  check_email: string;
  wants_to_sell: string;
};

interface PersonalInfoProps {
  unsupportedSellError: string | null;
  setUnsupportedSellError: (error: string | null) => void;
  keepMyCarSelectorEnabled: boolean;
  sellOnlyPanel: boolean;
  onBuyout: () => Promise<void>;
  buyoutLoading: boolean;
}

const PersonalInfo = ({
  unsupportedSellError,
  setUnsupportedSellError,
  keepMyCarSelectorEnabled,
  sellOnlyPanel,
  onBuyout,
  buyoutLoading,
}: PersonalInfoProps) => {
  const [accessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const { decodedToken } = useJwt<JWTToken>(accessToken || '');
  const accessTokenEmail = getEmailFromJWT(decodedToken);
  const step = useStep();
  const resolveRouting = usePostLienholderRouting();
  const phoneNumberPlacementVariation = useFlag(LDFlags.PHONE_NUMBER_PLACEMENT_VARIATION);
  const showEmailNote = useFlag(LDFlags.SHOW_EMAIL_NOTE);
  const isSellBlockedLienholder = useIsSellBlockedLienholder();

  const { updateState, isExpanded } = useLEAccordionItemContext();
  const { info } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const tempInfoData = info?.data;
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const checkOptOut = useOptOutLookup();
  const [checkExistingAccountByEmail] = useCheckExistingAccountByEmailLazyQuery({
    fetchPolicy: 'network-only',
  });
  const [logoutConfirmModalOpen, setLogoutConfirmModalOpen] = useState(false);

  const { lastPanelGettingStartedRequired, lastPanelGettingStartedOptional } =
    PHONE_NUMBER_PLACEMENT_VARIATIONS;

  const phoneIsRequired = phoneNumberPlacementVariation === lastPanelGettingStartedRequired;
  const showPhone =
    phoneIsRequired || phoneNumberPlacementVariation === lastPanelGettingStartedOptional;

  const validationSchema = Yup.object({
    email: emailValidationRequired,
    phone_number: Yup.string().when(['check_email', 'email'], {
      is: (check_email: string, email: string) =>
        (check_email && check_email === email) || (showPhone && phoneIsRequired),
      then: phoneValidationRequired,
      otherwise: phoneValidationOptional,
    }),
    password: Yup.string().when(['check_email', 'email'], {
      is: (check_email: string, email: string) => check_email && check_email === email,
      then: Yup.string().required('Please enter your password'),
    }),
  });

  const onSubmitForm = async (values: FormValues, { setFieldValue }: FormikHelpers<FormValues>) => {
    const { data: accountExists } = await checkExistingAccountByEmail({
      variables: {
        email: values.email,
      },
    });

    if (accountExists?.checkExistingAccountByEmail) {
      setFieldValue('check_email', values.email);
      rudderanalytics.track(RudderEvent.AccountExists, { email: values.email });
      return;
    }

    const data = {
      email: values.email,
      phone_number: values.phone_number,
      ...(keepMyCarSelectorEnabled && {
        wants_to_sell: sellOnlyPanel || values.wants_to_sell === 'true',
      }),
    };

    const { data: updatedInfo } = await updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data,
        },
      },
    });
    const updatedTempInfoData = updatedInfo?.temporaryInfoUpdate?.data ?? tempInfoData ?? null;

    const wantsToSell = keepMyCarSelectorEnabled && updatedTempInfoData?.wants_to_sell === true;

    if (wantsToSell && isSellBlockedLienholder(updatedTempInfoData)) {
      // Inactivity import withholds wants_to_sell leads, so keep a blocked lead a buyout.
      await updateTemporaryInfo({
        variables: { info: { id: info?.id, data: { wants_to_sell: false } } },
      });
      setUnsupportedSellError(
        sellOnlyPanel
          ? UNSUPPORTED_LIENHOLDER_SELL_MSG
          : UNSUPPORTED_LIENHOLDER_SELL_SELECT_KEEP_MSG,
      );
      return;
    }
    setUnsupportedSellError(null);

    await checkOptOut({ email: values.email, phone: values.phone_number });
    trackLead({
      tempInfoId: info?.id,
      email: values.email ?? undefined,
      phone_number: values.phone_number ?? undefined,
    });

    updateState({ status: 'complete' });

    const { skipPayoffAccordion } = await resolveRouting({
      wantsToSell,
      tempInfoData: updatedTempInfoData,
    });

    step.moveNext({ skipPayoffAccordion, wantsToSell });
  };

  return (
    <>
      <Formik<FormValues>
        enableReinitialize
        initialValues={{
          email: tempInfoData?.email ?? accessTokenEmail ?? '',
          phone_number: tempInfoData?.phone_number ?? '',
          check_email: '',
          wants_to_sell: getInitialWantsToSell(tempInfoData),
        }}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmitForm}
      >
        {({ isSubmitting, values, setFieldValue }) => {
          return (
            <Form>
              <VStack textAlign="center">
                {sellOnlyPanel ? (
                  <Text>We'll email you as buyers make offers on your car.</Text>
                ) : (
                  keepMyCarSelectorEnabled && (
                    <KeepMyCarSelector values={values} setFieldValue={setFieldValue} />
                  )
                )}
                <Input
                  _container={{
                    w: '100%',
                    mx: { base: 0, md: 1 },
                    mb: 2,
                    px: keepMyCarSelectorEnabled ? 10 : 1,
                  }}
                  name="email"
                  placeholder="Email"
                  label="EMAIL ADDRESS"
                  labelProps={{ opacity: '1 !important' }}
                  helperText={
                    showEmailNote &&
                    "You'll use this email to set up your account. Your privacy matters to us—your email will never be shared or sold."
                  }
                />
                {showPhone && (
                  <Input
                    label="Phone Number"
                    name="phone_number"
                    placeholder={`Phone Number ${phoneIsRequired ? '' : '(Optional)'}`}
                    mask="(999) 999-9999"
                    type="tel"
                    _container={{ w: '100%', mx: { base: 0, md: 1 }, px: 1, mb: '3 !important' }}
                  />
                )}
                {values.check_email && values.check_email === values.email && (
                  <VStack mb={2}>
                    <Text color="leaseEndRed" fontWeight="bold" fontSize="16px" textAlign="center">
                      Based on the email provided, it appears you already have an account with us.
                    </Text>
                    <Link as={ReactRouterLink} to={ACCOUNT_LOGIN}>
                      Login now
                    </Link>
                  </VStack>
                )}
              </VStack>
              {(!values.email || values.check_email !== values.email) && (
                <Center mt="30px">
                  <PrimaryButton loading={isSubmitting} isDisabled={buyoutLoading} type="submit">
                    NEXT: YOUR LEASE!
                  </PrimaryButton>
                </Center>
              )}
              {unsupportedSellError && (
                <Text color="leaseEndRed" fontWeight="bold" mt={4} textAlign="center">
                  {unsupportedSellError}
                </Text>
              )}
              {sellOnlyPanel && (
                <Text mt={4} textAlign="center">
                  Want to keep your car?{' '}
                  <Button variant="link" isLoading={buyoutLoading} onClick={onBuyout}>
                    Buy it out instead
                  </Button>
                </Text>
              )}
            </Form>
          );
        }}
      </Formik>
      <LogoutConfirmModal
        isOpen={logoutConfirmModalOpen}
        handleClose={() => setLogoutConfirmModalOpen(false)}
      />
    </>
  );
};

export default PersonalInfo;
